import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";

gsap.registerPlugin(Draggable, InertiaPlugin);

export default class CarouselHandler {
	init() {
		gsap.utils.toArray('[data-carousel="container"]').forEach((e) => {

			if ( e.querySelector('[data-carousel="carousel"]') && e.querySelector('[data-carousel="slide"]')) {
	
				const wrapper = e.querySelector('[data-carousel="carousel"]')
				let slides = gsap.utils.toArray(wrapper.querySelectorAll('[data-carousel="slide"]'));
				const transition = e.getAttribute('data-carousel-transition') || 'slide';
				const autoplay = e.getAttribute('data-carousel-autoplay') === 'true';
				const draggable = e.getAttribute('data-carousel-drag') !== 'false'; // Default true unless explicitly set to false
				const duration = parseInt(e.getAttribute('data-carousel-duration')) || 3000; // Used for fade transition interval timing
				const speed = parseFloat(e.getAttribute('data-carousel-speed')) || null; // Used for ticker mode scroll speed (multiplier)
				const delay = e.getAttribute('data-carousel-delay') ? parseInt(e.getAttribute('data-carousel-delay')) : null; // Delay between slides, null = no delay for slide type

				// Check if this is ticker mode: slide transition, autoplay on, drag off, no delay
				const isTickerMode = transition === 'slide' && autoplay === true && draggable === false && delay === null;

				// Duplicate slides for ticker mode if they don't fill the window
				if (isTickerMode) {
					const originalSlides = [...slides];
					let totalWidth = 0;

					// Calculate total width of all slides
					originalSlides.forEach(slide => {
						totalWidth += slide.offsetWidth;
					});

					const windowWidth = window.innerWidth;

					// Duplicate slides until they span at least the full window width
					while (totalWidth < windowWidth) {
						originalSlides.forEach(slide => {
							const clone = slide.cloneNode(true);
							wrapper.appendChild(clone);
							totalWidth += slide.offsetWidth;
						});
					}

					// Update slides array to include duplicates
					slides = gsap.utils.toArray(wrapper.querySelectorAll('[data-carousel="slide"]'));
				}
	
				const nextButton = e.querySelector('[data-carousel="button-next"]');
				const prevButton = e.querySelector('[data-carousel="button-prev"]');
			
				const totalElement = e.querySelector('[data-slide-count="total"]');
				const stepElement = e.querySelector('[data-slide-count="step"]');
				const stepsParent = stepElement ? stepElement.parentElement : null;
	
				let activeElement;
				const totalSlides = slides.length;
				let allSteps = [];
	
				// Update total slides text, prepend 0 if less than 10
				if (totalElement) {
					totalElement.textContent = totalSlides < 10 ? `0${totalSlides}` : totalSlides;
				}
	
				// Create step elements dynamically if step element exists
				if (stepElement && stepsParent) {
					stepsParent.innerHTML = ''; // Clear any existing steps
					slides.forEach((_, index) => {
						const stepClone = stepElement.cloneNode(true); // Clone the single step
						stepClone.setAttribute('data-step-number', index + 1 < 10 ? `0${index + 1}` : index + 1);
						stepClone.classList.add('cursor-pointer'); // Make clickable
						// Set initial state - first step is active, others are inactive
						if (index === 0) {
							stepClone.classList.add('bg-gradient-to-r', 'from-secondary-1', 'to-primary-1', '!w-16');
						} else {
							stepClone.classList.add('!bg-grey-4');
						}
						stepsParent.appendChild(stepClone); // Append to the parent container
					});
					
					// Dynamically generated steps
					allSteps = stepsParent.querySelectorAll('[data-slide-count="step"]');
				}
	
				// Function to update step classes
				const updateStepClasses = (activeIndex) => {
					if (allSteps.length > 0) {
						allSteps.forEach((step, index) => {
							if (index === activeIndex) {
								// Make active
								step.classList.add('active');
							} else {
								// Make inactive
								step.classList.remove('active');
							}
						});
					}
				};

				let loop;

				if (transition === 'fade') {
					// Fade transition
					let currentIndex = 0;
					let autoplayInterval;
					let isTransitioning = false;
					
					// Set initial state - first slide visible, others hidden
					slides.forEach((slide, index) => {
						gsap.set(slide, { autoAlpha: index === 0 ? 1 : 0, position: index === 0 ? 'relative' : 'absolute' });
					});
					
					const resetSlideStates = () => {
						// Force reset all slides to ensure only current is visible
						slides.forEach((slide, index) => {
							if (index === currentIndex) {
								gsap.set(slide, { autoAlpha: 1, position: 'relative' });
							} else {
								gsap.set(slide, { autoAlpha: 0, position: 'absolute' });
							}
						});
					};
					
					const fadeToSlide = (index) => {
						if (index === currentIndex || isTransitioning) return;
						
						isTransitioning = true;
						const currentSlide = slides[currentIndex];
						const targetSlide = slides[index];
						
						// Kill any existing tweens to prevent conflicts
						gsap.killTweensOf([currentSlide, targetSlide]);
						
						const tl = gsap.timeline({
							onComplete: () => {
								isTransitioning = false;
								resetSlideStates(); // Ensure clean state after transition
							}
						});
						
						tl.to(currentSlide, { autoAlpha: 0, duration: 0.5, ease: "power2.out" })
						tl.set(currentSlide, { position: 'absolute' })
						tl.set(targetSlide, { position: 'relative' })
						tl.to(targetSlide, { autoAlpha: 1, duration: 0.5, ease: "power2.out" }, 0.5)
						
						currentIndex = index;
						
						// Update step classes
						updateStepClasses(index);
					};
					
					const startAutoplay = () => {
						if (autoplay && !autoplayInterval) {
							autoplayInterval = setInterval(() => {
								fadeToSlide((currentIndex + 1) % slides.length);
							}, duration);
						}
					};
					
					const stopAutoplay = () => {
						if (autoplayInterval) {
							clearInterval(autoplayInterval);
							autoplayInterval = null;
						}
					};
					
					// Create simple navigation object
					loop = {
						next: () => {
							stopAutoplay();
							fadeToSlide((currentIndex + 1) % slides.length);
							startAutoplay();
						},
						previous: () => {
							stopAutoplay();
							fadeToSlide(currentIndex === 0 ? slides.length - 1 : currentIndex - 1);
							startAutoplay();
						},
						toIndex: (index) => {
							stopAutoplay();
							fadeToSlide(index);
							startAutoplay();
						}
					};
					
					// Start autoplay if enabled
					startAutoplay();
				} else {
					// Slide transition (original)
					loop = horizontalLoop(slides, {
						paused: !(autoplay && delay === null), // Only start playing immediately if autoplay is on and no delay is set
						repeat: -1,
						draggable: draggable,
						center: false,
						speed: speed, // Pass speed for ticker mode
						onChange: (element, index) => {
							// Update step classes
							updateStepClasses(index);
						}
					});

					// Add continuous or interval-based autoplay for slide transition
					if (autoplay) {
						if (delay !== null) {
							// Use interval-based autoplay if delay is set
							let slideAutoplayInterval;
							const startSlideAutoplay = () => {
								if (!slideAutoplayInterval) {
									slideAutoplayInterval = setInterval(() => {
										loop.next({ ease: "power2.out", duration: 0.8 });
									}, delay);
								}
							};

							const stopSlideAutoplay = () => {
								if (slideAutoplayInterval) {
									clearInterval(slideAutoplayInterval);
									slideAutoplayInterval = null;
								}
							};

							// Override loop methods to handle autoplay restart
							const originalNext = loop.next;
							const originalPrevious = loop.previous;
							const originalToIndex = loop.toIndex;

							loop.next = (vars) => {
								stopSlideAutoplay();
								originalNext(vars);
								startSlideAutoplay();
							};

							loop.previous = (vars) => {
								stopSlideAutoplay();
								originalPrevious(vars);
								startSlideAutoplay();
							};

							loop.toIndex = (index, vars) => {
								stopSlideAutoplay();
								originalToIndex(index, vars);
								startSlideAutoplay();
							};

							// Start autoplay
							startSlideAutoplay();
						} else {
							// Use continuous ticker with no delay
							loop.play();

							// Override loop methods to handle pause/resume for user interactions
							const originalNext = loop.next;
							const originalPrevious = loop.previous;
							const originalToIndex = loop.toIndex;

							loop.next = (vars) => {
								loop.pause();
								originalNext(vars);
								setTimeout(() => loop.play(), (vars && vars.duration || 1) * 1000);
							};

							loop.previous = (vars) => {
								loop.pause();
								originalPrevious(vars);
								setTimeout(() => loop.play(), (vars && vars.duration || 1) * 1000);
							};

							loop.toIndex = (index, vars) => {
								loop.pause();
								originalToIndex(index, vars);
								setTimeout(() => loop.play(), (vars && vars.duration || 1) * 1000);
							};
						}
					}
				}

				// Button event listeners
				if (nextButton) {
					nextButton.addEventListener("click", () => {
						if (transition === 'fade') {
							loop.next();
						} else {
							loop.next({ ease: "power4", duration: 1 });
						}
					});
				}
				
				if (prevButton) {
					prevButton.addEventListener("click", () => {
						if (transition === 'fade') {
							loop.previous();
						} else {
							loop.previous({ ease: "power4", duration: 1 });
						}
					});
				}
				
				// Add click listeners to step indicators if they exist
				if (allSteps.length > 0) {
					allSteps.forEach((step, index) => {
						step.addEventListener("click", () => {
							if (transition === 'fade') {
								loop.toIndex(index);
							} else {
								loop.toIndex(index, { ease: "power3", duration: 0.725 });
							}
						});
					});
				}
				
				// Listen for tab change events from custom pagination (only if this carousel has the attribute)
				const customPaginationId = e.getAttribute('data-custom-pagination');
				if (customPaginationId) {
					e.addEventListener('tabChanged', (event) => {
						const targetIndex = event.detail.index;
						if (transition === 'fade') {
							loop.toIndex(targetIndex);
						} else {
							loop.toIndex(targetIndex, { ease: "power3", duration: 0.725 });
						}
					});
				}
			}
		});
	
		function horizontalLoop(items, config) {
			let timeline;
			items = gsap.utils.toArray(items);
			config = config || {};
			gsap.context(() => {
				let onChange = config.onChange,
					lastIndex = 0,
					tl = gsap.timeline({
						repeat: config.repeat,
						onUpdate: onChange && function() {
							let i = tl.closestIndex();
							if (lastIndex !== i) {
								lastIndex = i;
								onChange(items[i], i);
							}
						},
						paused: config.paused,
						defaults: { ease: "none" },
						onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
					}),
					length = items.length,
					startX = items[0].offsetLeft,
					times = [],
					widths = [],
					spaceBefore = [],
					xPercents = [],
					curIndex = 0,
					indexIsDirty = false,
					center = config.center,
					pixelsPerSecond,
					snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
					timeOffset = 0,
					container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
					totalWidth,
					getTotalWidth = () => items[length - 1].offsetLeft + xPercents[length - 1] / 100 * widths[length - 1] - startX + spaceBefore[0] + items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], "scaleX") + (parseFloat(config.paddingRight) || 0),
					populateWidths = () => {
						let b1 = container.getBoundingClientRect(),
							b2;
						items.forEach((el, i) => {
							widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
							xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
							b2 = el.getBoundingClientRect();
							spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
							b1 = b2;
						});
						gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
							xPercent: i => xPercents[i]
						});
						totalWidth = getTotalWidth();
						pixelsPerSecond = (config.speed || 1) * 100;
					},
					timeWrap,
					populateOffsets = () => {
						timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
						center && times.forEach((t, i) => {
							times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
						});
					},
					getClosest = (values, value, wrap) => {
						let i = values.length,
							closest = 1e10,
							index = 0,
							d;
						while (i--) {
							d = Math.abs(values[i] - value);
							if (d > wrap / 2) {
								d = wrap - d;
							}
							if (d < closest) {
								closest = d;
								index = i;
							}
						}
						return index;
					},
					populateTimeline = () => {
						let i, item, curX, distanceToStart, distanceToLoop;
						tl.clear();
						for (i = 0; i < length; i++) {
							item = items[i];
							curX = xPercents[i] / 100 * widths[i];
							distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
							distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
							tl.to(item, { xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond }, 0)
								.fromTo(item, { xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100) }, { xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false }, distanceToLoop / pixelsPerSecond)
								.add("label" + i, distanceToStart / pixelsPerSecond);
							times[i] = distanceToStart / pixelsPerSecond;
						}
						timeWrap = gsap.utils.wrap(0, tl.duration());
					},
					refresh = (deep) => {
						let progress = tl.progress();
						tl.progress(0, true);
						populateWidths();
						deep && populateTimeline();
						populateOffsets();
						deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true);
					},
					onResize = () => refresh(true),
					proxy;
				gsap.set(items, { x: 0 });
				populateWidths();
				populateTimeline();
				populateOffsets();
				window.addEventListener("resize", onResize);
	
				function toIndex(index, vars) {
					vars = vars || {};
					(Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
					let newIndex = gsap.utils.wrap(0, length, index),
						time = times[newIndex];
					if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
						time += tl.duration() * (index > curIndex ? 1 : -1);
					}
					if (time < 0 || time > tl.duration()) {
						vars.modifiers = { time: timeWrap };
					}
					curIndex = newIndex;
					vars.overwrite = true;
					gsap.killTweensOf(proxy);
					return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
				}
				tl.toIndex = (index, vars) => toIndex(index, vars);
				tl.closestIndex = setCurrent => {
					let index = getClosest(times, tl.time(), tl.duration());
					if (setCurrent) {
						curIndex = index;
						indexIsDirty = false;
					}
					return index;
				};
				tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
				tl.next = vars => toIndex(tl.current() + 1, vars);
				tl.previous = vars => toIndex(tl.current() - 1, vars);
				tl.times = times;
				tl.progress(1, true).progress(0, true); // pre-render for performance
				if (config.reversed) {
					tl.vars.onReverseComplete();
					tl.reverse();
				}
				if (config.draggable && typeof(Draggable) === "function") {
					proxy = document.createElement("div")
					let wrap = gsap.utils.wrap(0, 1),
						ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX, wasPlaying,
						align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
						syncIndex = () => tl.closestIndex(true);
					typeof(InertiaPlugin) === "undefined" && console.warn("InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club");
					draggable = Draggable.create(proxy, {
						trigger: items[0].parentNode,
						type: "x",
						onPressInit() {
							let x = this.x;
							gsap.killTweensOf(tl);
							wasPlaying = !tl.paused();
							tl.pause();
							startProgress = tl.progress();
							refresh();
							ratio = 1 / totalWidth;
							initChangeX = (startProgress / -ratio) - x;
							gsap.set(proxy, { x: startProgress / -ratio });
						},
						onDrag: align,
						onThrowUpdate: align,
						overshootTolerance: 0,
						inertia: true,
						snap(value) {
							if (Math.abs(startProgress / -ratio - this.x) < 10) {
								return lastSnap + initChangeX
							}
							let time = -(value * ratio) * tl.duration(),
								wrappedTime = timeWrap(time),
								snapTime = times[getClosest(times, wrappedTime, tl.duration())],
								dif = snapTime - wrappedTime;
							Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
							lastSnap = (time + dif) / tl.duration() / -ratio;
							return lastSnap;
						},
						onRelease() {
							syncIndex();
							draggable.isThrowing && (indexIsDirty = true);
						},
						onThrowComplete: () => {
							syncIndex();
							wasPlaying && tl.play();
						}
					})[0];
					tl.draggable = draggable;
				}
				tl.closestIndex(true);
				lastIndex = curIndex;
				onChange && onChange(items[curIndex], curIndex);
				timeline = tl;
				return () => window.removeEventListener("resize", onResize);
			});
			return timeline;
		};
	}
}
